import React from 'react';
import { Link } from 'react-router';
import classnames from 'classnames';

import Logo from 'images/icon/om_digital_logo.png';
import YuanyuLogo from 'images/icon/yylogo.svg';

import Navigation from 'components/molecules/Navigation';
import Division from 'components/atoms/Division';
import SearchInput, { SimpleSearchInput } from 'components/molecules/SearchInput';
import CategoryAccordion from 'components/molecules/CategoryAccordion';
import Accordion from 'components/molecules/Accordion';

import SearchIcon from 'images/icon/search.inline.svg';
import UserIcon from 'images/icon/user.inline.svg';
import FavoriteIcon from 'images/icon/favorite.inline.svg';
import ShoppingCartIcon from 'images/icon/shopping-cart.inline.svg';
import MenuIcon from 'images/icon/menu.inline.svg';

import { useMedia } from 'util/event';
import { useBoolean, useReactRouter } from 'util/hook';
import { sendSearch } from 'util/analytics';

import { useNavigationCategory } from 'model/category';
import { useToken } from 'model/auth';
import { useCollectProduct } from 'model/product';
import { useCartProduct } from 'model/cart';

import styles from './index.css';

const { API_ENDPOINT, MAINTAINING } = process.env;

const WrapAuthLink = ({ className, children, onClick = () => {}, to, number = 0 }) => {
	const [token] = useToken();

	if (token === '') {
		return (
			<a
				className={className}
				href={`${API_ENDPOINT}/login?referral=EC`}
				target="_blank"
				rel="noopener noreferrer"
				onClick={onClick}
			>
				{children}
				{number > 0 && <div className={styles.number}>{number}</div>}
			</a>
		);
	}

	return (
		<Link className={className} to={to} onClick={onClick}>
			{children}
			{number > 0 && <div className={styles.number}>{number}</div>}
		</Link>
	);
};

const WrapMobileIcon = ({ isOpen, closeMenu, toggleSearch, collectNum, cartNum }) => {
	if (isOpen) {
		return (
			<div className={styles.right}>
				<WrapAuthLink to={MAINTAINING ? 'user/bonus' : '/user/order'} onClick={closeMenu}>
					<UserIcon />
				</WrapAuthLink>
				<WrapAuthLink to="/collection" onClick={closeMenu} number={collectNum}>
					<FavoriteIcon />
				</WrapAuthLink>
				<Division className={styles.division} vertical />
			</div>
		);
	}

	return (
		<div className={styles.right}>
			<Link to="/cart">
				<ShoppingCartIcon />
				{cartNum > 0 && <div className={styles.number}>{cartNum}</div>}
			</Link>
			<button onClick={toggleSearch} type="button">
				<SearchIcon />
			</button>
		</div>
	);
};

const HeaderMobile = ({ categoryList, onSearch, cartNum, collectNum }) => {
	const [isOpen, { toggle, setFalse: closeMenu }] = useBoolean();
	const [isSearchOpen, { toggle: toggleSearch, setFalse: closeSearch }] = useBoolean();
	const [{ pathname }] = useReactRouter();
	const [token] = useToken();

	const toggleMenu = toggleValue => {
		if (toggleValue) {
			closeSearch();
		}
		toggle(toggleValue);
	};

	return (
		<header className={styles.header}>
			<div className={styles.top}>
				<button type="button" onClick={toggleMenu}>
					<MenuIcon />
				</button>
				{!isOpen && (
					<Link to="/" className={styles.center} onClick={closeMenu}>
						<img src={Logo} alt="logo" height="20" />
					</Link>
				)}
				<WrapMobileIcon
					isOpen={isOpen}
					closeMenu={closeMenu}
					toggleSearch={toggleSearch}
					cartNum={cartNum}
					collectNum={collectNum}
				/>
			</div>
			<Division />
			{isOpen && (
				<div className={styles.subMobileNavigation}>
					<CategoryAccordion categoryList={categoryList} isNavi closeMenu={closeMenu} />
					<Accordion
						className={styles.userArea}
						contentClassName={styles.content}
						headClassName={styles.head}
						defaultHeight={62}
						title="會員專區"
						onClickTitle={closeMenu}
					>
						<WrapAuthLink
							className={classnames(styles.item, {
								[styles.active]: pathname === '/user/order',
							})}
							to="/user/order"
							onClick={closeMenu}
						>
							訂單記錄
						</WrapAuthLink>
						<Link
							className={classnames(styles.item, {
								[styles.active]: pathname === '/service/refund',
							})}
							to="/service/refund"
							onClick={closeMenu}
						>
							退貨說明
						</Link>
						<WrapAuthLink
							className={classnames(styles.item, {
								[styles.active]: pathname === '/user/bonus',
							})}
							to="/user/bonus"
							onClick={closeMenu}
						>
							紅利點數
						</WrapAuthLink>
						<WrapAuthLink
							className={classnames(styles.item, {
								[styles.active]: pathname === '/user/coupon',
							})}
							to="/user/coupon"
							onClick={closeMenu}
						>
							優惠券
						</WrapAuthLink>
						<a
							className={classnames(styles.item)}
							href={token === '' ? `${API_ENDPOINT}/login?referral=EC` : `${API_ENDPOINT}/member`}
							target="_blank"
							rel="noopener noreferrer"
							onClick={closeMenu}
						>
							會員專區
						</a>
					</Accordion>
				</div>
			)}
			{isSearchOpen && (
				<div className={styles.subMobileNavigation}>
					<SimpleSearchInput
						className={styles.input}
						placeholder="輸入您所要搜尋的產品關鍵字"
						onSearch={onSearch}
					/>
				</div>
			)}
		</header>
	);
};

const HeaderDesktop = ({ categoryList, onSearch, cartNum, collectNum }) => (
	<header className={styles.header}>
		<div className={styles.top}>
			<Link to="/">
				<img src={Logo} alt="logo" height="20" />
			</Link>
			<div className={styles.right}>
				<SearchInput className={styles.search} onSearch={onSearch} />
				<Division className={styles.division} vertical />
				<WrapAuthLink to={MAINTAINING ? '/user/bonus' : '/user/order'}>
					<UserIcon />
				</WrapAuthLink>
				<WrapAuthLink to="/collection" number={collectNum}>
					<FavoriteIcon />
				</WrapAuthLink>
				<Link to="/cart">
					{cartNum > 0 && <div className={styles.number}>{cartNum}</div>}
					<ShoppingCartIcon />
				</Link>
				<Division className={styles.division} vertical />
				<a href="https://www.yuanyu.tw/camera/" target="_blank" rel="noopener noreferrer">
					<img src={YuanyuLogo} alt="logo" height="20" />
				</a>
			</div>
		</div>
		<Division />
		<Navigation className={styles.navigation} categoryList={categoryList} />
		<Division />
	</header>
);

const Header = () => {
	const media = useMedia();
	const [{ category: categoryList }] = useNavigationCategory();
	const [, { push }] = useReactRouter();
	const [
		{
			product: { list: collect },
		},
	] = useCollectProduct();
	const [{ list: cart, additional }] = useCartProduct();

	const collectNum = collect.length;
	const additionalNum = Object.keys(additional).length;
	const cartNum = cart.length + additionalNum;
	const onSearch = value => {
		sendSearch({ search_string: value });
		push(`/search?keyword=${value}`);
	};

	return media === 'desktop' ? (
		<HeaderDesktop
			categoryList={categoryList}
			onSearch={onSearch}
			cartNum={cartNum}
			collectNum={collectNum}
		/>
	) : (
		<HeaderMobile
			categoryList={categoryList}
			onSearch={onSearch}
			cartNum={cartNum}
			collectNum={collectNum}
		/>
	);
};

export default Header;
